import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useApp, API } from "../context/AppContext";
import { Wand2, Loader2, Sparkles } from "lucide-react";

const EXAMPLES_ES = [
  "Somos 5 amigos con unos 25€ por cabeza, nos apetece tapas y algo cultural el próximo sábado por Sevilla",
  "Escapada con los peques a menos de 40 km de Cuenca este domingo, presupuesto 20€ por persona",
  "Aventura fuerte para 4 personas cerca de Madrid, todo el día, 60€/persona",
];
const EXAMPLES_EN = [
  "We're 5 friends with ~25€ each, want tapas and something cultural next Saturday in Seville",
  "Getaway with kids under 40 km from Cuenca this Sunday, 20€/person",
  "Adventure day for 4 near Madrid, full day, 60€/person",
];

export default function NaturalLanguageInput({ onParsed }) {
  const { t, lang } = useApp();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const examples = lang === "es" ? EXAMPLES_ES : EXAMPLES_EN;

  const interpret = async () => {
    if (!text.trim()) return;
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/plans/parse`, { text: text.trim(), language: lang });
      onParsed(data);
      toast.success(t.interpretedOk);
    } catch {
      toast.error(t.interpretError);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-[#FFF4EC] to-[#EDF7EF] rounded-3xl border border-[#E7E0D5] p-5 sm:p-6 mb-5">
      <div className="flex items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest font-mono font-semibold text-[#D95338] bg-white/70 border border-[#FDCC9D] px-2.5 py-1 rounded-full">
          <Sparkles className="w-3 h-3" />{t.nlBadge}
        </span>
        <h2 className="text-sm sm:text-base font-semibold text-stone-800">{t.nlTitle}</h2>
      </div>

      <textarea
        data-testid="input-nl"
        value={text}
        onChange={(e) => setText(e.target.value)}
        rows={2}
        placeholder={t.nlPlaceholder}
        className="w-full px-4 py-3 rounded-2xl bg-white border border-[#E7E0D5] focus:border-[#D95338] focus:ring-2 focus:ring-[#D95338]/15 outline-none text-sm resize-none"
      />

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <button
          type="button"
          data-testid="button-interpret"
          onClick={interpret}
          disabled={!text.trim() || loading}
          className="inline-flex items-center gap-2 text-sm font-semibold px-5 py-2.5 rounded-full bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-40 active:scale-95 transition-all"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {loading ? t.interpreting : t.interpret}
        </button>
        <span className="text-xs text-stone-500 hidden sm:inline">{t.nlHint}</span>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        {examples.map((ex, i) => (
          <button
            key={i}
            type="button"
            data-testid={`nl-example-${i}`}
            onClick={() => setText(ex)}
            className="text-[11px] px-3 py-1.5 rounded-full bg-white/70 hover:bg-white border border-[#E7E0D5] text-stone-600 hover:text-stone-900 transition-all text-left max-w-full truncate"
            title={ex}
          >
            "{ex.slice(0, 60)}{ex.length > 60 ? "..." : ""}"
          </button>
        ))}
      </div>
    </div>
  );
}
